import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Loader2, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

export function LaborCreateCalendarModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  year,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const defaultName = `Calendario ${factoryCode || ''} ${year}`.trim();
  const [name, setName] = useState(defaultName);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setName(`Calendario ${factoryCode || ''} ${year}`.trim());
      setError(null);
    }
  }, [isOpen, factoryCode, year]);

  const mutation = useMutation({
    mutationFn: () =>
      laborScheduleService.createCalendar({
        factoryId,
        year,
        name: name.trim() || defaultName,
      }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['labor-calendars', factoryId] });
      onSuccess?.(data);
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo crear el calendario');
    },
  });

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={mutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
        disabled={mutation.isPending || !name.trim()}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Crear calendario
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Crear calendario laboral"
      icon={CalendarPlus}
      footer={footer}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Se usará la fábrica seleccionada en el header. No hace falta indicar el ID manualmente.
      </p>

      <dl className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
          <dt className="text-gray-500 text-xs mb-1">Fábrica</dt>
          <dd className="text-white font-medium">{factoryCode || '—'}</dd>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
          <dt className="text-gray-500 text-xs mb-1">Año del calendario</dt>
          <dd className="text-white font-medium">{year}</dd>
        </div>
      </dl>

      <label htmlFor="labor-new-cal-name" className="block text-sm text-gray-300 mb-2">
        Nombre <span className="text-red-400">*</span>
      </label>
      <input
        id="labor-new-cal-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={mutation.isPending}
        className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
      />
    </LaborCalendarModalShell>
  );
}
